'use client';

import { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { IconPlayerPlay, IconEye, IconLoader2, IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import type { SourceRunStatus } from '@/lib/sources/source-types';
import { filterSources, SourceStatusFilter, formatExactDate } from '@/lib/sources/source-utils';
import { formatRelativeTime } from '@/lib/projects/project-utils';
import { StatusBadge } from '@/components/sources/StatusBadge';
import { cn } from '@/lib/utils';

interface SourcesTableProps {
  sources: SourceRunStatus[];
  onRunSource: (sourceId: string) => void;
  onOpen: (source: SourceRunStatus) => void;
  runningSourceIds?: Set<string>;
  searchQuery?: string;
}

const STATUS_FILTERS: { label: string; value: SourceStatusFilter }[] = [
  { label: 'All', value: 'all' },
  { label: 'OK', value: 'ok' },
  { label: 'Warning', value: 'warning' },
  { label: 'Failed', value: 'error' },
];

const PAGE_SIZE = 10;

export function SourcesTable({
  sources,
  onRunSource,
  onOpen,
  runningSourceIds,
  searchQuery = '',
}: SourcesTableProps) {
  const [statusFilter, setStatusFilter] = useState<SourceStatusFilter>('all');
  const [page, setPage] = useState(1);

  const filtered = useMemo(
    () => filterSources(sources, searchQuery, statusFilter),
    [sources, searchQuery, statusFilter]
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pagedFiltered = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleFilterChange = (f: SourceStatusFilter) => {
    setStatusFilter(f);
    setPage(1);
  };

  const goToPage = (p: number) => setPage(Math.max(1, Math.min(p, totalPages)));

  return (
    <div>
      {/* Filter row */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="flex items-center rounded-md border bg-muted/30 p-0.5">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => handleFilterChange(f.value)}
              className={cn(
                'px-3 py-1 text-xs font-medium rounded-sm transition-colors',
                statusFilter === f.value
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="border rounded-lg bg-card">
        <TooltipProvider delayDuration={300}>
        <div className="overflow-auto">
          <Table className="table-fixed">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-[30%]">Source</TableHead>
                <TableHead className="w-[10%]">Status</TableHead>
                <TableHead className="w-[15%]">Last Run</TableHead>
                <TableHead className="w-[10%]">Records</TableHead>
                <TableHead className="w-[25%]">Message</TableHead>
                <TableHead className="w-[10%]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pagedFiltered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-12 text-center">
                    <p className="text-sm text-muted-foreground">
                      No sources match your filters.
                    </p>
                  </TableCell>
                </TableRow>
              ) : (
                pagedFiltered.map((source: SourceRunStatus, index: number) => {
                  const isRunning = runningSourceIds?.has(source.sourceId);
                  return (
                    <tr
                      key={source.sourceId}
                      className={cn(
                        'group cursor-pointer border-b border-border/60',
                        'hover:bg-muted/30 focus:bg-muted/30 focus:outline-none transition-colors',
                        index % 2 === 1 ? 'bg-muted/10' : 'bg-background'
                      )}
                      tabIndex={0}
                      onClick={() => onOpen(source)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onOpen(source);
                        }
                      }}
                    >
                      <TableCell className="py-2.5 align-top">
                        <div className="min-w-0">
                          <p className="font-medium truncate">{source.name}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {source.domain}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="py-2.5 align-top">
                        {isRunning ? (
                          <Badge variant="outline" className="border-blue-500/50 text-blue-600">
                            <IconLoader2 className="size-3 mr-1 animate-spin" />
                            Running…
                          </Badge>
                        ) : (
                          <StatusBadge status={source.status} />
                        )}
                      </TableCell>
                      <TableCell className="py-2.5 align-top">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="text-sm tabular-nums">
                              {formatRelativeTime(source.lastRunAt)}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            {formatExactDate(source.lastRunAt)}
                          </TooltipContent>
                        </Tooltip>
                      </TableCell>
                      <TableCell className="py-2.5 align-top text-sm text-muted-foreground">
                        –
                      </TableCell>
                      <TableCell className="py-2.5 align-top">
                        {source.message ? (
                          <p className="text-xs text-muted-foreground line-clamp-1">
                            {source.message}
                          </p>
                        ) : (
                          <span className="text-xs text-muted-foreground">–</span>
                        )}
                      </TableCell>
                      <TableCell className="py-2.5 align-top text-right">
                        <div className="opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity flex items-center gap-1 justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            disabled={isRunning}
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              onRunSource(source.sourceId);
                            }}
                          >
                            <IconPlayerPlay className="size-3.5 mr-1" />
                            Run
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              onOpen(source);
                            }}
                          >
                            <IconEye className="size-3.5 mr-1" />
                            View
                          </Button>
                        </div>
                      </TableCell>
                    </tr>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
        </TooltipProvider>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t">
          <span className="text-xs text-muted-foreground">
            Page {page} of {totalPages} · {filtered.length} sources
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              disabled={page === 1}
              onClick={() => goToPage(page - 1)}
              aria-label="Previous page"
            >
              <IconChevronLeft className="h-4 w-4" />
            </Button>
            {getPaginationPages(page, totalPages).map((p, i) =>
              p === '…' ? (
                <span key={`e-${i}`} className="px-1 text-muted-foreground text-sm">…</span>
              ) : (
                <Button
                  key={p}
                  variant={p === page ? 'outline' : 'ghost'}
                  size="icon"
                  className="h-8 w-8 text-xs"
                  onClick={() => goToPage(p as number)}
                  aria-current={p === page ? 'page' : undefined}
                >
                  {p}
                </Button>
              )
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              disabled={page === totalPages}
              onClick={() => goToPage(page + 1)}
              aria-label="Next page"
            >
              <IconChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function getPaginationPages(current: number, total: number): (number | '…')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | '…')[] = [1];
  if (current > 3) pages.push('…');
  for (let p = Math.max(2, current - 1); p <= Math.min(total - 1, current + 1); p++) pages.push(p);
  if (current < total - 2) pages.push('…');
  pages.push(total);
  return pages;
}
